import React, { useEffect, useRef } from "react";
import { TfiReload } from "react-icons/tfi";

const STYLES = `
  @keyframes amcp-pulse {
    0%, 100% { opacity: 0.5; transform: scale(1); }
    50%       { opacity: 1;   transform: scale(1.08); }
  }
  @keyframes amcp-ring {
    0%   { transform: scale(0.85); opacity: 0.6; }
    100% { transform: scale(1.55); opacity: 0; }
  }
  @keyframes amcp-fadein {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .amcp-wrap {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100%;
    width: 100%;
    background: #080d18;
    padding: 32px 16px;
    box-sizing: border-box;
  }

  .amcp-card {
    position: relative;
    max-width: 520px;
    width: 100%;
    background: #0c1428;
    border: 1px solid #1a2540;
    border-radius: 20px;
    padding: 52px 44px 44px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    animation: amcp-fadein 0.45s ease both;
    box-shadow: 0 0 0 1px #1a2540, 0 24px 64px rgba(0,0,0,0.5);
    overflow: hidden;
  }

  /* Subtle top-edge glow */
  .amcp-card::before {
    content: "";
    position: absolute;
    top: 0; left: 50%;
    transform: translateX(-50%);
    width: 60%;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(239,68,68,0.4), transparent);
  }

  /* Icon area */
  .amcp-icon-wrap {
    position: relative;
    width: 88px;
    height: 88px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 28px;
  }

  .amcp-icon-bg {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: rgba(239, 68, 68, 0.08);
    border: 1.5px solid rgba(239, 68, 68, 0.2);
  }

  /* Pulsing ring */
  .amcp-icon-ring {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 1.5px solid rgba(239, 68, 68, 0.35);
    animation: amcp-ring 2s ease-out infinite;
  }
  .amcp-icon-ring:nth-child(2) {
    animation-delay: 0.7s;
  }

  .amcp-icon-svg {
    position: relative;
    z-index: 1;
    animation: amcp-pulse 2.4s ease-in-out infinite;
  }

  .amcp-title {
    font-size: 22px;
    font-weight: 700;
    color: #e8edf7;
    letter-spacing: -0.4px;
    margin: 0 0 14px;
    line-height: 1.3;
  }

  .amcp-desc {
    font-size: 14.5px;
    color: #7a8fb5;
    line-height: 1.65;
    margin: 0 0 32px;
    max-width: 400px;
  }

  /* Step hints */
  .amcp-steps {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    margin-bottom: 32px;
  }

  .amcp-step {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #162035;
    border: 1px solid #1a2540;
    border-radius: 10px;
    padding: 11px 14px;
    text-align: left;
  }

  .amcp-step-num {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: rgba(239, 68, 68, 0.12);
    border: 1px solid rgba(239, 68, 68, 0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    color: #f87171;
  }

  .amcp-step-text {
    font-size: 13px;
    color: #8fa3c8;
    line-height: 1.4;
  }

  .amcp-step-text strong {
    color: #c4d0ea;
    font-weight: 600;
  }

  /* Reload button */
  .amcp-btn {
    all: unset;
    box-sizing: border-box;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 13px 36px;
    border-radius: 10px;
    background: #162035;
    border: 1px solid #243354;
    color: #c4d0ea;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.2px;
    transition: background 0.18s, border-color 0.18s, color 0.18s, box-shadow 0.18s;
  }

  .amcp-btn:hover {
    background: #1e2f4a;
    border-color: #4ade80;
    color: #e8edf7;
    box-shadow: 0 0 0 1px rgba(74, 222, 128, 0.15), 0 4px 16px rgba(74,222,128,0.08);
  }

  .amcp-hint {
    font-size: 12px;
    color: #3d5070;
    margin-top: 20px;
  }
`;

const CameraIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#f87171"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 7L16 12l7 5V7z" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    <line x1="1" y1="1" x2="23" y2="23" stroke="#ef4444" strokeWidth="1.8" />
  </svg>
);

let _injected = false;

const AllowMicroCameraPermissionBox = () => {
  const ref = useRef(false);

  useEffect(() => {
    if (_injected || ref.current) return;
    ref.current = true;
    _injected = true;
    const el = document.createElement("style");
    el.textContent = STYLES;
    document.head.appendChild(el);
  }, []);

  return (
    <div className="amcp-wrap">
      <div className="amcp-card">
        {/* Animated icon */}
        <div className="amcp-icon-wrap">
          <div className="amcp-icon-bg" />
          <div className="amcp-icon-ring" />
          <div className="amcp-icon-ring" />
          <span className="amcp-icon-svg">
            <CameraIcon />
          </span>
        </div>

        <h4 className="amcp-title">Camera &amp; Microphone Blocked</h4>

        <p className="amcp-desc">
          Your browser is blocking access to your camera or microphone.
          Follow the steps below to grant permission, then reload.
        </p>

        {/* Step-by-step hints */}
        <div className="amcp-steps">
          <div className="amcp-step">
            <span className="amcp-step-num">1</span>
            <span className="amcp-step-text">
              Click the <strong>lock icon</strong> or <strong>camera icon</strong> in your browser's address bar
            </span>
          </div>
          <div className="amcp-step">
            <span className="amcp-step-num">2</span>
            <span className="amcp-step-text">
              Set <strong>Camera</strong> and <strong>Microphone</strong> to <strong>Allow</strong>
            </span>
          </div>
          <div className="amcp-step">
            <span className="amcp-step-num">3</span>
            <span className="amcp-step-text">
              Click <strong>Reload Page</strong> below to apply the changes
            </span>
          </div>
        </div>

        <button className="amcp-btn" onClick={() => window.location.reload()}>
          <TfiReload size={15} />
          Reload Page
        </button>

        <p className="amcp-hint">
          Using Chrome? Try <strong style={{ color: "#4d6490" }}>chrome://settings/content/camera</strong>
        </p>
      </div>
    </div>
  );
};

export default AllowMicroCameraPermissionBox;